import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler } from "chart.js";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import { usePermissions } from "../../context/PermissionContext";
import StatCard from "../../components/admin/StatCard";
import DashboardHeader from "../../components/admin/DashboardHeader";
import SectionHeader from "../../components/admin/SectionHeader";
import QuickActionCard from "../../components/admin/QuickActionCard";
import ModuleCard from "../../components/admin/ModuleCard";
import AttentionCard from "../../components/admin/AttentionCard";
import AdminFilterBar from "../../components/admin/AdminFilterBar";
import TrendChartCard from "../../components/admin/TrendChartCard";
import WorkloadRow from "../../components/admin/WorkloadRow";
import ProjectHealthCard from "../../components/admin/ProjectHealthCard";
import ActivityRow from "../../components/admin/ActivityRow";
import { getTatInfo, formatDuration } from "../tasks/tatUtils";
import { taskDisplayTitle } from "../../utils/taskDisplay";
import "../../components/dashboard/DashboardComponents.css";
import "../../components/admin/AdminDashboardHome.css";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler);

// Reports nav gate mirrors Sidebar.js's own canAny(...) list exactly (see
// src/components/AdminLayout/Sidebar.js) so this page never links to a
// route the signed-in admin cannot actually open.
const REPORTS_PERMISSIONS = [
  { moduleKey: "reports", actionKey: "view_tasks" },
  { moduleKey: "reports", actionKey: "view_task_tat" },
  { moduleKey: "reports", actionKey: "view_contributions" },
  { moduleKey: "reports", actionKey: "view_forms" },
  { moduleKey: "reports", actionKey: "view_form_results" },
  { moduleKey: "reports", actionKey: "view_uat" },
  { moduleKey: "reports", actionKey: "view_approvals" },
  { moduleKey: "reports", actionKey: "view_workload" },
];

const settledValue = (result) => (result.status === "fulfilled" ? result.value : null);

const DONE_STATUSES = new Set(["completed", "closed"]);
const CANCELLED_STATUSES = new Set(["cancelled", "rejected"]);
// Tasks by Status bar colour -- semantic accent per §18 (green=done,
// orange=pending, blue=in progress, red=cancelled/rejected); any other
// status key reported by the backend falls back to the primary teal.
const STATUS_TONE = { completed: "success", closed: "success", pending: "warning", in_progress: "info", cancelled: "danger", rejected: "danger" };
const normalizeStatus = (v) => String(v || "unknown").trim().toLowerCase().replace(/\s+/g, "_");
const titleCase = (v) => String(v || "-").replace(/_/g, " ").replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());
const isDoneStatus = (s) => DONE_STATUSES.has(s);
const isCancelledStatus = (s) => CANCELLED_STATUSES.has(s);
const startOfDay = (v) => { const d = new Date(v); d.setHours(0, 0, 0, 0); return d; };
const endOfDay = (v) => { const d = new Date(v); d.setHours(23, 59, 59, 999); return d; };
const isSameDay = (a, b) => { if (!a) return false; const d = new Date(a); return !Number.isNaN(d.getTime()) && startOfDay(d).getTime() === startOfDay(b).getTime(); };
const daysBetween = (a, b) => Math.max(0, Math.floor((b.getTime() - a.getTime()) / 86400000));
const isOverdue = (row, now) => {
  if (!row.due_at) return false;
  const s = normalizeStatus(row.status);
  if (isDoneStatus(s) || isCancelledStatus(s)) return false;
  const due = new Date(row.due_at);
  return !Number.isNaN(due.getTime()) && due < now;
};

function periodRange(period, customFrom, customTo, now) {
  if (period === "today") return [startOfDay(now), endOfDay(now)];
  if (period === "7d") { const d = new Date(now); d.setDate(d.getDate() - 6); return [startOfDay(d), endOfDay(now)]; }
  if (period === "30d") { const d = new Date(now); d.setDate(d.getDate() - 29); return [startOfDay(d), endOfDay(now)]; }
  if (period === "month") { const first = new Date(now.getFullYear(), now.getMonth(), 1); return [startOfDay(first), endOfDay(now)]; }
  if (period === "custom") return [customFrom ? startOfDay(new Date(customFrom)) : null, customTo ? endOfDay(new Date(customTo)) : null];
  return [null, null];
}

function formatActivityTime(v, now) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  if (isSameDay(d, now)) return `Today · ${time}`;
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
  if (isSameDay(d, yesterday)) return `Yesterday · ${time}`;
  return `${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} · ${time}`;
}

const SHORTCUT_VISIBLE_LIMIT = 6;

export default function AdminDashboard() {
  const { user } = useContext(AuthContext) || {};
  const { can, canAny, loading: permsLoading } = usePermissions();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [pageError, setPageError] = useState(false);

  const [orgSummary, setOrgSummary] = useState(null);
  const [formSummary, setFormSummary] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [projectTotal, setProjectTotal] = useState(null);
  const [unitTotal, setUnitTotal] = useState(null);
  const [productTotal, setProductTotal] = useState(null);
  const [allocTotal, setAllocTotal] = useState(null);
  const [approvalConfigured, setApprovalConfigured] = useState(true);

  // Full org task list -- powers every /tasks-derived section (Performance,
  // Task Health, Workload, Project Health, Task Breakdown, Recent Activity,
  // and the "unassigned"/"approaching SLA" attention items). Same
  // unpaginated GET /tasks call AdminTasksPage.jsx already makes; gated the
  // same way that page gates it.
  const canViewTasks = can("tasks", "view");
  const [tasks, setTasks] = useState([]);
  const [tasksError, setTasksError] = useState(false);

  const loadDashboard = useCallback(async () => {
    const calls = [
      api.get("/dashboards/organization-summary"),
      api.get("/form-assignment-dashboard/summary"),
      api.get("/admin/access-management/users"),
      api.get("/projects", { params: { pageSize: 1 } }),
      api.get("/units"),
      api.get("/products"),
      api.get("/alloc"),
    ];
    if (canViewTasks) calls.push(api.get("/tasks"));

    const results = await Promise.allSettled(calls);
    const [orgRes, formRes, usersRes, projectsRes, unitsRes, productsRes, allocRes, tasksRes] = results;

    const org = settledValue(orgRes);
    const summaryCards = org?.data?.data?.summaryCards || null;
    setOrgSummary(summaryCards);
    // approvalTotals is either present (real, even zero) or absent from the
    // payload entirely -- only the latter means "not configured" (spec
    // §7/§20). A fetch failure is NOT the same as "not configured", so this
    // only reflects a successful response that genuinely omits the field.
    setApprovalConfigured(orgRes.status !== "fulfilled" ? true : summaryCards?.approvalTotals !== undefined);

    const form = settledValue(formRes);
    setFormSummary(form?.data?.data || null);

    const usersPayload = settledValue(usersRes);
    const userRows = Array.isArray(usersPayload?.data?.data) ? usersPayload.data.data : null;
    setUserStats(userRows ? { total: userRows.length, active: userRows.filter((u) => u.is_active).length } : null);

    const projects = settledValue(projectsRes);
    const projMeta = projects?.data?.meta;
    setProjectTotal(typeof projMeta?.total === "number" ? projMeta.total : Array.isArray(projects?.data?.data) ? projects.data.data.length : null);

    const units = settledValue(unitsRes);
    setUnitTotal(Array.isArray(units?.data) ? units.data.length : null);

    const products = settledValue(productsRes);
    setProductTotal(Array.isArray(products?.data) ? products.data.length : null);

    const allocations = settledValue(allocRes);
    setAllocTotal(Array.isArray(allocations?.data) ? allocations.data.length : null);

    if (canViewTasks) {
      const taskData = settledValue(tasksRes);
      const rows = Array.isArray(taskData?.data) ? taskData.data : Array.isArray(taskData?.data?.data) ? taskData.data.data : null;
      setTasks(rows ? rows.map((t) => ({ ...t, status: normalizeStatus(t.status) })) : []);
      setTasksError(tasksRes.status === "rejected");
    }

    setPageError(results.every((r) => r.status === "rejected"));
    setLastUpdated(new Date());
  }, [canViewTasks]);

  useEffect(() => {
    if (permsLoading) return;
    setLoading(true);
    loadDashboard().finally(() => setLoading(false));
  }, [permsLoading, loadDashboard]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await loadDashboard();
    } finally {
      setRefreshing(false);
    }
  };

  const goTo = (path) => () => navigate(path);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => new Date(), [tasks]);

  /* ── Global filter bar -- drives the /tasks-derived sections only ── */
  const [filterPeriod, setFilterPeriod] = useState("all");
  const [filterCustomFrom, setFilterCustomFrom] = useState("");
  const [filterCustomTo, setFilterCustomTo] = useState("");
  const [filterOrg, setFilterOrg] = useState("ALL");
  const [filterProject, setFilterProject] = useState("ALL");
  const [filterUnit, setFilterUnit] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");

  const orgOptions = useMemo(() => Array.from(new Set(tasks.map((t) => t.company_name || t.organization_name).filter(Boolean))).sort(), [tasks]);
  const projectOptions = useMemo(() => Array.from(new Set(tasks.map((t) => t.project_name).filter(Boolean))).sort(), [tasks]);
  const unitOptions = useMemo(() => Array.from(new Set(tasks.map((t) => t.unit_name || t.department_name).filter(Boolean))).sort(), [tasks]);
  const statusOptions = useMemo(() => Array.from(new Set(tasks.map((t) => titleCase(t.status)))).sort(), [tasks]);
  const hasActiveFilters = filterPeriod !== "all" || filterOrg !== "ALL" || filterProject !== "ALL" || filterUnit !== "ALL" || filterStatus !== "ALL";
  const resetFilters = () => { setFilterPeriod("all"); setFilterCustomFrom(""); setFilterCustomTo(""); setFilterOrg("ALL"); setFilterProject("ALL"); setFilterUnit("ALL"); setFilterStatus("ALL"); };

  const filteredTasks = useMemo(() => {
    const [from, to] = periodRange(filterPeriod, filterCustomFrom, filterCustomTo, now);
    return tasks.filter((t) => {
      if (from || to) {
        const created = t.created_at ? new Date(t.created_at) : null;
        if (!created || Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
      }
      if (filterOrg !== "ALL" && (t.company_name || t.organization_name) !== filterOrg) return false;
      if (filterProject !== "ALL" && t.project_name !== filterProject) return false;
      if (filterUnit !== "ALL" && (t.unit_name || t.department_name) !== filterUnit) return false;
      if (filterStatus !== "ALL" && titleCase(t.status) !== filterStatus) return false;
      return true;
    });
  }, [tasks, filterPeriod, filterCustomFrom, filterCustomTo, filterOrg, filterProject, filterUnit, filterStatus, now]);

  /* ── Needs Your Attention -- existing org-summary-driven items kept,
     extended with two new items the /tasks fetch enables. ── */
  const unassignedCount = useMemo(() => canViewTasks ? tasks.filter((t) => !t.assigned_to_name && !isDoneStatus(t.status) && !isCancelledStatus(t.status)).length : 0, [tasks, canViewTasks]);
  const approachingSlaCount = useMemo(() => canViewTasks ? tasks.filter((t) => !isDoneStatus(t.status) && !isCancelledStatus(t.status) && getTatInfo(t, now).tone === "amber").length : 0, [tasks, canViewTasks, now]);

  const attentionItems = [
    can("dashboards", "view_organization") && orgSummary?.taskTotals?.tasksOverdue > 0 && {
      key: "overdue-tasks", icon: "fa-triangle-exclamation", tone: "danger",
      count: orgSummary.taskTotals.tasksOverdue, label: "Overdue Tasks",
      description: "Tasks past their due date requiring attention.", to: "/admin/tasks",
    },
    canViewTasks && approachingSlaCount > 0 && {
      key: "approaching-sla", icon: "fa-hourglass-half", tone: "warning",
      count: approachingSlaCount, label: "Approaching SLA",
      description: "Open tasks due within the next 2 hours.", to: "/admin/tasks",
    },
    can("form_reviews", "view_review_queue") && formSummary?.admin?.pendingReviews > 0 && {
      key: "pending-forms", icon: "fa-file-circle-exclamation", tone: "warning",
      count: formSummary.admin.pendingReviews, label: "Pending Forms",
      description: "Forms awaiting review.", to: "/admin/form-review-queue",
    },
    can("dashboards", "view_organization") && orgSummary?.approvalTotals?.pendingCount > 0 && {
      key: "pending-approvals", icon: "fa-hourglass-half", tone: "warning",
      count: orgSummary.approvalTotals.pendingCount, label: "Pending Approvals",
      description: "Approval requests awaiting a decision.", to: "/admin/my-task-approvals",
    },
    canViewTasks && unassignedCount > 0 && {
      key: "unassigned-tasks", icon: "fa-user-slash", tone: "info",
      count: unassignedCount, label: "Unassigned Tasks",
      description: "Open tasks with no assignee yet.", to: "/admin/tasks",
    },
    can("dashboards", "view_organization") && !approvalConfigured && {
      key: "approval-not-configured", icon: "fa-gear", tone: "info",
      count: null, label: "Approval Workflow Not Configured",
      description: "Set up an approval policy to start routing requests.", to: "/admin/task-approval-policies",
    },
  ].filter(Boolean);

  /* ── Operational KPIs -- decoupled per the reality check (§7/§19) ── */
  const kpiCards = [
    can("access_management", "view_users") && {
      key: "users", label: "Total Users", value: userStats?.total ?? null,
      subtext: userStats ? `${userStats.active} enabled` : undefined,
      icon: "fa-users", accent: "info",
    },
    {
      key: "projects", label: "Projects", value: projectTotal,
      icon: "fa-diagram-project", accent: "info",
    },
    can("dashboards", "view_organization") && {
      key: "open-tasks", label: "Open Tasks", value: orgSummary?.taskTotals?.tasksOpen ?? null,
      subtext: orgSummary?.taskTotals?.tasksOverdue !== undefined ? `${orgSummary.taskTotals.tasksOverdue} overdue` : undefined,
      icon: "fa-folder-open",
      accent: orgSummary?.taskTotals?.tasksOpen > 0 && orgSummary?.taskTotals?.tasksOpen === orgSummary?.taskTotals?.tasksOverdue ? "danger" : "warning",
    },
    {
      key: "pending-forms", label: "Pending Forms", value: formSummary?.admin?.pendingReviews ?? null,
      subtext: formSummary?.admin?.pendingReviews === 0 ? "No forms awaiting review" : formSummary?.admin?.formReviewQueue !== undefined ? `${formSummary.admin.formReviewQueue} in review queue` : "Awaiting review",
      icon: "fa-clock", accent: formSummary?.admin?.pendingReviews > 0 ? "warning" : "success",
      // Dynamic Forms visibility audit, §8: same drill-down destination the
      // "Needs Your Attention" copy of this exact metric already uses
      // (goTo(item.to) above) -- this card should not be a dead number
      // while that one is clickable.
      onClick: can("form_reviews", "view_review_queue") ? goTo("/admin/form-review-queue") : undefined,
    },
    can("dashboards", "view_organization") && {
      key: "pending-approvals",
      label: "Pending Approvals",
      value: !approvalConfigured ? "Not configured" : orgSummary?.approvalTotals?.pendingCount ?? null,
      subtext: !approvalConfigured ? undefined : orgSummary?.approvalTotals?.pendingCount === 0 ? "No pending approvals" : orgSummary?.approvalTotals?.requestsRaised !== undefined ? `${orgSummary.approvalTotals.requestsRaised} requests raised` : undefined,
      icon: "fa-hourglass-half", accent: !approvalConfigured ? "info" : orgSummary?.approvalTotals?.pendingCount > 0 ? "warning" : "success",
    },
    can("dashboards", "view_organization") && {
      key: "task-completion", label: "Tasks Completed", value: orgSummary?.taskTotals?.tasksCompleted ?? null,
      subtext: "All-time", icon: "fa-circle-check", accent: "success",
    },
    {
      key: "units", label: "Organizational Units", value: unitTotal,
      subtext: "Departments & branches", icon: "fa-building", accent: "info",
    },
    {
      key: "inventory", label: "Inventory Items", value: productTotal,
      subtext: productTotal !== null && allocTotal !== null ? `${allocTotal} allocated · ${Math.max(0, productTotal - allocTotal)} available` : allocTotal !== null ? `${allocTotal} active allocations` : undefined,
      icon: "fa-boxes-stacked", accent: "info",
    },
  ].filter(Boolean);

  /* ── Operational Performance trend ── */
  const [trendGranularity, setTrendGranularity] = useState("weekly");
  const trend = useMemo(() => {
    const bucketCount = trendGranularity === "daily" ? 14 : 12;
    const buckets = [];
    if (trendGranularity === "monthly") {
      for (let i = bucketCount - 1; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        buckets.push({ label: d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" }), from: startOfDay(d), to: endOfDay(new Date(d.getFullYear(), d.getMonth() + 1, 0)) });
      }
    } else {
      const stepMs = trendGranularity === "daily" ? 86400000 : 7 * 86400000;
      for (let i = bucketCount - 1; i >= 0; i--) {
        const to = new Date(now.getTime() - i * stepMs);
        const from = trendGranularity === "daily" ? to : new Date(to.getTime() - 6 * 86400000);
        buckets.push({ label: to.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }), from: startOfDay(from), to: endOfDay(to) });
      }
    }
    const inBucket = (v, b) => { if (!v) return false; const d = new Date(v); return !Number.isNaN(d.getTime()) && d >= b.from && d <= b.to; };
    const created = buckets.map((b) => filteredTasks.filter((t) => inBucket(t.created_at, b)).length);
    const completed = buckets.map((b) => filteredTasks.filter((t) => isDoneStatus(t.status) && inBucket(t.completed_at || t.updated_at, b)).length);
    const overdue = buckets.map((b) => filteredTasks.filter((t) => isOverdue(t, now) && inBucket(t.created_at, b)).length);
    return {
      labels: buckets.map((b) => b.label),
      datasets: [
        { label: "Tasks Created", data: created, borderColor: "#377fa3", backgroundColor: "rgba(55,127,163,0.12)", fill: true, tension: 0.35 },
        { label: "Tasks Completed", data: completed, borderColor: "#159a70", backgroundColor: "rgba(21,154,112,0.12)", fill: true, tension: 0.35 },
        { label: "Overdue Tasks", data: overdue, borderColor: "#dc4c5a", backgroundColor: "rgba(220,76,90,0.12)", fill: true, tension: 0.35 },
      ],
      hasAnyData: created.some(Boolean) || completed.some(Boolean) || overdue.some(Boolean),
    };
  }, [filteredTasks, trendGranularity, now]);

  /* ── Task Health ── */
  const resolutionDurations = useMemo(() => filteredTasks
    .filter((t) => isDoneStatus(t.status) && t.created_at && (t.completed_at || t.updated_at))
    .map((t) => new Date(t.completed_at || t.updated_at).getTime() - new Date(t.created_at).getTime())
    .filter((ms) => Number.isFinite(ms) && ms >= 0), [filteredTasks]);
  const avgResolutionMs = resolutionDurations.length ? resolutionDurations.reduce((a, b) => a + b, 0) / resolutionDurations.length : null;
  const slaRows = useMemo(() => filteredTasks.filter((t) => isDoneStatus(t.status) && t.due_at), [filteredTasks]);
  const onTimePct = slaRows.length ? Math.round((slaRows.filter((t) => getTatInfo(t, now).tone === "green").length / slaRows.length) * 100) : null;
  const openTasksForHealth = useMemo(() => filteredTasks.filter((t) => !isDoneStatus(t.status) && !isCancelledStatus(t.status)), [filteredTasks]);
  const overdueRate = filteredTasks.length ? Math.round((filteredTasks.filter((t) => isOverdue(t, now)).length / filteredTasks.length) * 100) : 0;
  const approachingDeadlineCount = useMemo(() => openTasksForHealth.filter((t) => getTatInfo(t, now).tone === "amber").length, [openTasksForHealth, now]);
  const oldestOpenTask = useMemo(() => openTasksForHealth.filter((t) => t.created_at).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0] || null, [openTasksForHealth]);

  /* ── Workload by User/Team ── */
  const workloadRows = useMemo(() => {
    const map = new Map();
    filteredTasks.forEach((t) => {
      const name = t.assigned_to_name || "Unassigned";
      if (!map.has(name)) map.set(name, { name, open: 0, overdue: 0, completed: 0 });
      const row = map.get(name);
      if (isDoneStatus(t.status)) row.completed += 1;
      else if (!isCancelledStatus(t.status)) row.open += 1;
      if (isOverdue(t, now)) row.overdue += 1;
    });
    const rows = [...map.values()].filter((r) => r.name !== "Unassigned");
    const maxOpen = Math.max(1, ...rows.map((r) => r.open));
    return rows
      .map((r) => ({
        ...r,
        barPct: Math.round((r.open / maxOpen) * 100),
        status: r.overdue > 0 || r.open >= 5 ? "overloaded" : r.open === 0 ? "low" : "balanced",
      }))
      .sort((a, b) => b.open - a.open)
      .slice(0, 8);
  }, [filteredTasks, now]);

  /* ── Project Health ── */
  const projectHealthRows = useMemo(() => {
    const map = new Map();
    filteredTasks.forEach((t) => {
      if (!t.project_name) return;
      if (!map.has(t.project_name)) map.set(t.project_name, { name: t.project_name, open: 0, overdue: 0, completed: 0 });
      const row = map.get(t.project_name);
      if (isDoneStatus(t.status)) row.completed += 1;
      else if (!isCancelledStatus(t.status)) row.open += 1;
      if (isOverdue(t, now)) row.overdue += 1;
    });
    return [...map.values()].map((r) => {
      const total = r.open + r.completed;
      return { ...r, completionPct: total ? Math.round((r.completed / total) * 100) : 0 };
    }).sort((a, b) => (b.open + b.completed) - (a.open + a.completed));
  }, [filteredTasks, now]);

  /* ── Task Breakdown by status ── */
  const statusBreakdown = useMemo(() => {
    const map = new Map();
    filteredTasks.forEach((t) => map.set(t.status, (map.get(t.status) || 0) + 1));
    const total = filteredTasks.length || 1;
    return [...map.entries()].map(([key, count]) => ({ key, label: titleCase(key), count, pct: Math.round((count / total) * 100) })).sort((a, b) => b.count - a.count);
  }, [filteredTasks]);

  /* ── Recent Activity -- real task created/completed events only; no
     audit-log endpoint exists anywhere in this app for user/permission/
     form/inventory events, so this section is intentionally narrower than
     a full audit trail. ── */
  const recentActivity = useMemo(() => {
    const events = [];
    filteredTasks.forEach((t) => {
      if (t.created_at) events.push({ key: `${t.id}-created`, time: t.created_at, icon: "fa-circle-plus", tone: "info", text: `Task #${t.id} — "${taskDisplayTitle(t)}" was created` });
      if (isDoneStatus(t.status) && (t.completed_at || t.updated_at)) events.push({ key: `${t.id}-completed`, time: t.completed_at || t.updated_at, icon: "fa-circle-check", tone: "success", text: `Task #${t.id} — "${taskDisplayTitle(t)}" was completed` });
    });
    return events.sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 8);
  }, [filteredTasks]);

  /* ── Quick Actions -- trimmed to 3 primary + "More Actions"; an urgent
     action is temporarily promoted to the front when overdue work exists. ── */
  const overdueTotal = orgSummary?.taskTotals?.tasksOverdue ?? 0;
  const primaryQuickActions = [
    overdueTotal > 0 && can("dashboards", "view_organization") && { key: "review-overdue", icon: "fa-triangle-exclamation", label: "Review Overdue Tasks", to: "/admin/tasks", variant: "urgent" },
    can("users", "view") && { key: "add-user", icon: "fa-user-plus", label: "Add User", to: "/admin/users", variant: "secondary" },
    { key: "new-project", icon: "fa-diagram-project", label: "New Project", to: "/admin/projects", variant: "secondary" },
    can("tasks", "create") && { key: "create-task", icon: "fa-list-check", label: "Create Task", to: "/admin/tasks/create", variant: "primary" },
  ].filter(Boolean).slice(0, overdueTotal > 0 ? 4 : 3);
  const moreQuickActions = [
    can("forms", "view") && { key: "manage-forms", icon: "fa-file-circle-plus", label: "Manage Forms", to: "/admin/forms" },
    can("tasks", "assign") && { key: "assign-task", icon: "fa-user-check", label: "Assign Task", to: "/admin/assign-tasks" },
    // Task Approval Policies / Inventory Allocation carry no permission
    // gate anywhere else in the app either (see Sidebar.js's own
    // ungated `leaf(...)` entries for both routes) -- matching that,
    // not inventing a stricter check here.
    { key: "configure-approval", icon: "fa-shield-halved", label: "Configure Approval", to: "/admin/task-approval-policies" },
    { key: "allocate-inventory", icon: "fa-boxes-packing", label: "Allocate Inventory", to: "/admin/inventory/allocation" },
  ].filter(Boolean);

  const modules = [
    { key: "organization", icon: "fa-building", title: "Organization", description: "Manage units, companies, departments and designations.", to: "/admin/unit", accent: "info", count: unitTotal !== null ? `${unitTotal} unit${unitTotal === 1 ? "" : "s"}` : undefined },
    { key: "projects", icon: "fa-diagram-project", title: "Projects", description: "Manage projects, modules and project configuration.", to: "/admin/projects", accent: "indigo", count: projectTotal !== null ? `${projectTotal} active` : undefined },
    can("tasks", "view") && { key: "tasks", icon: "fa-list-check", title: "Tasks", description: "Configure task types, workflows and monitor execution.", to: "/admin/tasks", accent: "warning", count: orgSummary?.taskTotals?.tasksOpen !== undefined ? `${orgSummary.taskTotals.tasksOpen} open` : undefined },
    can("forms", "view") && { key: "forms", icon: "fa-file-lines", title: "Forms", description: "Create, publish and manage forms and assignments.", to: "/admin/forms", accent: "primary", count: formSummary?.admin?.pendingReviews !== undefined ? `${formSummary.admin.pendingReviews} pending review` : undefined },
    can("users", "view") && { key: "user-access", icon: "fa-users-gear", title: "User & Access", description: "Manage users, roles and permissions.", to: "/admin/users", accent: "success", count: userStats?.total !== undefined ? `${userStats.total} users` : undefined },
    can("dashboards", "view_organization") && { key: "performance-reports", icon: "fa-chart-line", title: "Performance & Reports", description: "View dashboards, scorecards and reports.", to: "/admin/organization-dashboard", accent: "purple" },
  ].filter(Boolean);

  /* ── Shortcuts (replaces the long Quick Links rail) ── */
  const shortcutGroups = [
    { title: "Dashboards", items: [
      { key: "my-performance", icon: "fa-gauge-high", label: "My Performance", to: "/admin/my-performance-dashboard" },
      can("dashboards", "view_team") && { key: "team-dashboard", icon: "fa-users", label: "Team Dashboard", to: "/admin/team-performance-dashboard" },
      can("dashboards", "view_project") && { key: "project-dashboard", icon: "fa-diagram-project", label: "Project Dashboard", to: "/admin/project-performance-dashboard" },
      can("dashboards", "view_organization") && { key: "organization-dashboard", icon: "fa-shield-halved", label: "Organisation Dashboard", to: "/admin/organization-dashboard" },
    ].filter(Boolean) },
    { title: "Reviews", items: [
      can("form_reviews", "view_review_queue") && { key: "form-review-queue", icon: "fa-rectangle-list", label: "Form Review Queue", to: "/admin/form-review-queue" },
      { key: "finance-queue", icon: "fa-sack-dollar", label: "Finance Queue", to: "/admin/accounts/finance" },
    ].filter(Boolean) },
    { title: "Reports", items: [
      canAny(REPORTS_PERMISSIONS) && { key: "reports", icon: "fa-chart-column", label: "Reports", to: "/admin/reports" },
      { key: "invoice-generated", icon: "fa-file-invoice-dollar", label: "Invoices", to: "/admin/accounts/invoice-generated" },
    ].filter(Boolean) },
    { title: "Access", items: [
      can("access_management", "view_users") && { key: "user-permissions", icon: "fa-user-lock", label: "User Permissions", to: "/admin/access-management/users" },
      can("access_management", "view_roles") && { key: "role-permissions", icon: "fa-shield-halved", label: "Role Permissions", to: "/admin/access-management/roles" },
    ].filter(Boolean) },
  ].filter((g) => g.items.length > 0);
  const totalShortcuts = shortcutGroups.reduce((s, g) => s + g.items.length, 0);
  const [shortcutsExpanded, setShortcutsExpanded] = useState(false);

  const showSkeleton = loading || permsLoading;

  return (
    <div className="unit-page dashboard-shell admin-home-page">
      <DashboardHeader name={user?.name} lastUpdated={lastUpdated} refreshing={refreshing} onRefresh={handleRefresh} />

      {showSkeleton ? (
        <div className="perf-skeleton-row">
          {[0, 1, 2, 3].map((i) => <div key={i} className="perf-skeleton-card" />)}
        </div>
      ) : pageError ? (
        <div className="perf-error-state">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
          Unable to load dashboard data.
          <button type="button" className="adh-retry-btn" onClick={handleRefresh}>Retry</button>
        </div>
      ) : (
        <>
          {canViewTasks && (
            <AdminFilterBar
              period={filterPeriod} onPeriodChange={setFilterPeriod}
              customFrom={filterCustomFrom} customTo={filterCustomTo} onCustomFromChange={setFilterCustomFrom} onCustomToChange={setFilterCustomTo}
              organisationOptions={orgOptions} organisation={filterOrg} onOrganisationChange={setFilterOrg}
              projectOptions={projectOptions} project={filterProject} onProjectChange={setFilterProject}
              unitOptions={unitOptions} unit={filterUnit} onUnitChange={setFilterUnit}
              statusOptions={statusOptions} status={filterStatus} onStatusChange={setFilterStatus}
              hasActiveFilters={hasActiveFilters} onReset={resetFilters}
            />
          )}

          {attentionItems.length > 0 && (
            <>
              <SectionHeader icon="fa-circle-exclamation" title="Needs Your Attention" />
              <div className="adh-attention-list adh-section-block">
                {attentionItems.map((item) => (
                  <AttentionCard key={item.key} icon={item.icon} tone={item.tone} count={item.count ?? ""} label={item.label} description={item.description} actionLabel="View" onAction={goTo(item.to)} />
                ))}
              </div>
            </>
          )}

          <div className="adh-kpi-grid ui-animate-stagger">
            {kpiCards.map((card) => (
              <StatCard key={card.key} label={card.label} value={card.value} subtext={card.subtext} icon={card.icon} accent={card.accent} onClick={card.onClick} />
            ))}
          </div>

          {(primaryQuickActions.length > 0 || moreQuickActions.length > 0) && (
            <>
              <SectionHeader icon="fa-bolt" title="Quick Actions" />
              <div className="adh-quick-actions-grid">
                {primaryQuickActions.map((action) => (
                  <QuickActionCard key={action.key} icon={action.icon} label={action.label} variant={action.variant === "urgent" ? "urgent" : action.variant} onClick={goTo(action.to)} />
                ))}
                {moreQuickActions.length > 0 && (
                  <details className="adh-more-actions">
                    <summary><i className="fa-solid fa-ellipsis" aria-hidden="true"></i>More Actions</summary>
                    <div className="adh-more-actions-grid">
                      {moreQuickActions.map((action) => (
                        <QuickActionCard key={action.key} icon={action.icon} label={action.label} variant="secondary" onClick={goTo(action.to)} />
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </>
          )}

          {canViewTasks && (
            <div className="adh-row-8-4">
              <TrendChartCard granularity={trendGranularity} onGranularityChange={setTrendGranularity} chartData={trend} loading={false} hasData={trend.hasAnyData} />

              <div className="adh-card">
                <div className="adh-card-head"><h3 className="adh-section-title"><i className="fa-solid fa-heart-pulse" aria-hidden="true"></i>Task Health</h3></div>
                <div className="adh-taskhealth-grid">
                  <div className="adh-taskhealth-metric"><span>On-Time Completion</span><strong>{onTimePct === null ? "—" : `${onTimePct}%`}</strong>{onTimePct === null && <small>No completed tasks with a due date yet.</small>}</div>
                  <div className="adh-taskhealth-metric"><span>Overdue Rate</span><strong>{overdueRate}%</strong></div>
                  <div className="adh-taskhealth-metric"><span>Average Completion Time</span><strong>{avgResolutionMs === null ? "—" : formatDuration(avgResolutionMs)}</strong></div>
                  <div className="adh-taskhealth-metric"><span>Approaching Deadline</span><strong>{approachingDeadlineCount}</strong></div>
                  <div className="adh-taskhealth-metric adh-taskhealth-metric--wide"><span>Oldest Open Task</span><strong>{oldestOpenTask ? `${taskDisplayTitle(oldestOpenTask)} · ${daysBetween(new Date(oldestOpenTask.created_at), now)}d old` : "None — nothing open"}</strong></div>
                </div>
              </div>
            </div>
          )}

          {canViewTasks && (
            <div className="adh-row-6-6">
              <div className="adh-card">
                <div className="adh-card-head">
                  <h3 className="adh-section-title"><i className="fa-solid fa-users-viewfinder" aria-hidden="true"></i>Workload by User/Team</h3>
                </div>
                {workloadRows.length ? (
                  <>
                    <div className="adh-workload-list">
                      {workloadRows.map((r) => (
                        <WorkloadRow key={r.name} name={r.name} openCount={r.open} overdueCount={r.overdue} completedCount={r.completed} status={r.status} barPct={r.barPct} />
                      ))}
                    </div>
                    <div className="adh-card-footer">
                      <span className="adh-workload-note">Status is a simple guide based on open and overdue task counts, not a configured system rule.</span>
                    </div>
                  </>
                ) : (
                  <div className="adh-card-empty"><i className="fa-solid fa-users" aria-hidden="true"></i><p>No assigned tasks in the current view.</p></div>
                )}
                {can("dashboards", "view_team") && (
                  <div className="adh-card-footer">
                    <button type="button" className="adh-card-footer-link" onClick={goTo("/admin/team-performance-dashboard")}>View Team Dashboard <i className="fa-solid fa-arrow-right" aria-hidden="true"></i></button>
                  </div>
                )}
              </div>

              <div className="adh-card">
                <div className="adh-card-head"><h3 className="adh-section-title"><i className="fa-solid fa-diagram-project" aria-hidden="true"></i>Project Health</h3></div>
                {projectHealthRows.length ? (
                  <div className="adh-project-list">
                    {projectHealthRows.map((p) => (
                      <ProjectHealthCard key={p.name} name={p.name} openCount={p.open} completedCount={p.completed} overdueCount={p.overdue} completionPct={p.completionPct} onClick={goTo("/admin/projects")} />
                    ))}
                  </div>
                ) : (
                  <div className="adh-card-empty"><i className="fa-solid fa-diagram-project" aria-hidden="true"></i><p>No tasks linked to a project in the current view.</p></div>
                )}
              </div>
            </div>
          )}

          {canViewTasks && statusBreakdown.length > 0 && (
            <div className="adh-card adh-section-block">
              <div className="adh-card-head"><h3 className="adh-section-title"><i className="fa-solid fa-chart-simple" aria-hidden="true"></i>Tasks by Status</h3></div>
              <div className="adh-status-list">
                {statusBreakdown.map((s) => (
                  <div key={s.key} className="adh-status-row">
                    <span className="adh-status-label">{s.label}</span>
                    <div className="adh-status-track"><div className={`adh-status-fill adh-status-fill--${STATUS_TONE[s.key] || "primary"}`} style={{ width: `${s.pct}%` }} /></div>
                    <span className="adh-status-count">{s.count} · {s.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="adh-row-8-4">
            <div className="adh-card">
              <div className="adh-card-head"><h3 className="adh-section-title"><i className="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>Recent Activity</h3></div>
              {!canViewTasks ? (
                <div className="adh-card-empty"><i className="fa-solid fa-lock" aria-hidden="true"></i><p>You don't have permission to view task activity.</p></div>
              ) : tasksError ? (
                <div className="adh-card-empty"><i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i><p>Recent activity could not be loaded.</p></div>
              ) : recentActivity.length ? (
                <>
                  <div className="adh-activity-list">
                    {recentActivity.map((e) => (
                      <ActivityRow key={e.key} icon={e.icon} tone={e.tone} text={e.text} timestamp={formatActivityTime(e.time, now)} />
                    ))}
                  </div>
                  <div className="adh-card-footer">
                    <button type="button" className="adh-card-footer-link" onClick={goTo("/admin/tasks")}>View All Tasks <i className="fa-solid fa-arrow-right" aria-hidden="true"></i></button>
                  </div>
                </>
              ) : (
                <div className="adh-card-empty"><i className="fa-solid fa-inbox" aria-hidden="true"></i><p>No recent task activity.</p></div>
              )}
            </div>

            <div className="adh-card">
              <div className="adh-card-head"><h3 className="adh-section-title"><i className="fa-solid fa-bolt" aria-hidden="true"></i>Shortcuts</h3></div>
              {(() => {
                let shown = 0;
                return shortcutGroups.map((group) => {
                  const visible = group.items.filter(() => shortcutsExpanded || shown < SHORTCUT_VISIBLE_LIMIT ? (shown += 1, true) : false);
                  if (!visible.length) return null;
                  return (
                    <div className="adh-shortcuts-group" key={group.title}>
                      <div className="adh-shortcuts-group-title">{group.title}</div>
                      <div className="adh-shortcuts-grid">
                        {visible.map((item) => (
                          <button key={item.key} type="button" className="adh-shortcut-item" onClick={goTo(item.to)}>
                            <span className="adh-shortcut-icon"><i className={`fa-solid ${item.icon}`} aria-hidden="true"></i></span>
                            <span className="adh-shortcut-label">{item.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
              {totalShortcuts > SHORTCUT_VISIBLE_LIMIT && (
                <div className="adh-card-footer">
                  <button type="button" className="adh-card-footer-link" onClick={() => setShortcutsExpanded((v) => !v)}>
                    {shortcutsExpanded ? "Show Less" : "View All Shortcuts"} <i className={`fa-solid fa-chevron-${shortcutsExpanded ? "up" : "down"}`} aria-hidden="true"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          {modules.length > 0 && (
            <>
              <SectionHeader icon="fa-table-cells-large" title="Management Modules" />
              <div className="adh-module-grid">
                {modules.map((module) => (
                  <ModuleCard key={module.key} icon={module.icon} title={module.title} description={module.description} accent={module.accent} count={module.count} onClick={goTo(module.to)} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
